import { useEffect, useState } from "react";
import api, { downloadUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Factory, Warehouse, Truck, Download, TrendingUp, Layers, FileText } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ icon: Ic, label, value, sub, tone = "amber", tid }) {
    const tones = {
        amber: "text-amber-300 bg-amber-500/10 border-amber-500/30",
        blue: "text-blue-300 bg-blue-500/10 border-blue-500/30",
        emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
        crimson: "text-red-300 bg-red-500/10 border-red-500/30",
    };
    return (
        <Card className="card p-5" data-testid={tid}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] tracking-[0.2em] font-mono text-slate-500 uppercase">{label}</div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-slate-50">{value}</div>
                    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
                </div>
                <div className={`inline-flex p-3 rounded-xl border ${tones[tone]}`}>
                    <Ic size={20} />
                </div>
            </div>
        </Card>
    );
}

export default function Dashboard() {
    const [gia, setGia] = useState([]);
    const [ordini, setOrdini] = useState([]);
    const [pedane, setPedane] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [g, o, p, d] = await Promise.all([
                    api.get("/giacenze"), api.get("/ordini"),
                    api.get("/pedane"), api.get("/documenti"),
                ]);
                setGia(g.data); setOrdini(o.data); setPedane(p.data); setDocs(d.data);
            } finally { setLoading(false); }
        })();
    }, []);

    const totProd = gia.reduce((s, r) => s + r.produzione, 0);
    const totMag = gia.reduce((s, r) => s + r.magazzino, 0);
    const aperti = ordini.filter(o => o.stato === "aperto").length;
    const doc30 = docs.filter(d => new Date(d.created_at) > new Date(Date.now()-30*86400000)).length;

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-500/80">CRUSCOTTO · LINEA SALDATURA</div>
                    <h1 className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-slate-50">Panoramica operativa</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-xl">
                        Giacenze in <span className="text-amber-300">cartoni</span> divise per <span className="text-amber-300">Reparto Produzione</span> e <span className="text-blue-300">Magazzino Finito</span>.
                    </p>
                </div>
                <a href={downloadUrl("/exports/giacenze.csv")} target="_blank" rel="noreferrer" data-testid="button-export-giacenze-csv">
                    <Button variant="outline" className="bg-transparent border-slate-700 gap-2">
                        <Download size={14} /> Giacenze CSV
                    </Button>
                </a>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Factory} label="Cart. in Produzione" value={totProd} tone="amber" sub="post-produzione, in attesa" tid="stat-produzione" />
                <Stat icon={Warehouse} label="Cart. a Magazzino" value={totMag} tone="blue" sub="a scaffale" tid="stat-magazzino" />
                <Stat icon={TrendingUp} label="Ordini aperti" value={aperti} tone="emerald" tid="stat-ordini-aperti" />
                <Stat icon={Truck} label="Documenti (30gg)" value={doc30} tone="crimson" tid="stat-documenti-30" />
            </div>

            <Card className="card overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <Package size={16} className="text-amber-400" />
                        <h2 className="font-display font-bold text-lg">Giacenze articoli</h2>
                    </div>
                    <Link to="/articoli" className="text-xs text-amber-400 hover:text-amber-300 font-mono tracking-wider" data-testid="link-gestisci-articoli">
                        GESTISCI →
                    </Link>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Descrizione</th>
                                <th className="text-right px-5 py-3 text-amber-400">Cart. Prod.</th>
                                <th className="text-right px-5 py-3 text-blue-400">Cart. Mag.</th>
                                <th className="text-right px-5 py-3">Tot. Cartoni</th>
                                <th className="text-right px-5 py-3 text-slate-500">Pz/Cart</th>
                                <th className="text-right px-5 py-3">Tot. Pezzi</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-giacenze">
                            {loading && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && gia.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500 text-sm">
                                    Nessuna giacenza. Crea articoli, ordini e chiusure di produzione per iniziare.
                                </td></tr>
                            )}
                            {gia.map(r => (
                                <tr key={r.article_id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{r.codice}</td>
                                    <td className="px-5 py-3 text-slate-200">{r.descrizione}</td>
                                    <td className="px-5 py-3 text-right font-mono text-amber-300">{r.produzione}</td>
                                    <td className="px-5 py-3 text-right font-mono text-blue-300">{r.magazzino}</td>
                                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-100">{r.totale}</td>
                                    <td className="px-5 py-3 text-right font-mono text-slate-500 text-xs">{r.pezzi_per_cartone}</td>
                                    <td className="px-5 py-3 text-right font-mono text-slate-300">{r.totale_pezzi}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
                <Card className="card p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Layers size={16} className="text-amber-400" />
                        <h3 className="font-display font-bold">Pedane attive</h3>
                    </div>
                    <div className="text-3xl font-display font-extrabold text-slate-50">{pedane.filter(p=>p.stato==="attiva").length}</div>
                    <Link to="/pedane" data-testid="link-pedane" className="text-xs text-amber-400 mt-2 inline-block hover:underline">Vai a Pedane →</Link>
                </Card>
                <Card className="card p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <FileText size={16} className="text-emerald-400" />
                        <h3 className="font-display font-bold">Ultimi documenti</h3>
                    </div>
                    <div className="space-y-1">
                        {docs.slice(0, 5).map(d => (
                            <div key={d.id} className="flex justify-between text-xs">
                                <span className="doc-code">{d.numero}</span>
                                <span className="text-slate-500">{new Date(d.created_at).toLocaleDateString("it-IT")}</span>
                            </div>
                        ))}
                        {docs.length === 0 && <div className="text-xs text-slate-500">Nessun documento</div>}
                    </div>
                </Card>
            </div>
        </div>
    );
}


